import React, { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { ImageLightbox } from "../ui/ImageLightbox";
import type { ComboDeal } from "../../types/content.types";
import { SectionBackgroundIcons } from "../ui/BackgroundIcons";
import { trackFacebookEvent, trackGA4Event } from "../../services/analytics";

interface ComboDealsSectionProps {
  heading: string;
  combos: ComboDeal[];
  ctaText: string;
  onCtaClick: (comboTitle: string) => void;
}

export function ComboDealsSection({
  heading,
  combos,
  ctaText,
  onCtaClick,
}: ComboDealsSectionProps) {
  const [lightboxImage, setLightboxImage] = useState<{
    url: string;
    alt: string;
  } | null>(null);

  const handleImageClick = (combo: ComboDeal) => {
    // Track ViewContent when someone views a combo image
    trackFacebookEvent("ViewContent", {
      content_name: combo.title,
      content_category: "Deal",
      content_type: "product",
    });

    trackGA4Event({
      eventName: "view_item",
      eventParams: {
        item_name: combo.title,
        item_category: "Deal",
      },
    });

    setLightboxImage({ url: combo.image, alt: combo.title });
  };

  // Preload all combo deal images on component mount
  useEffect(() => {
    combos.forEach((combo) => {
      const img = new Image();
      img.src = combo.image;
    });
  }, [combos]);
  return (
    <section className="bg-zinc-950 py-12 relative">
      <SectionBackgroundIcons variant="deals" />
      <div className="relative z-10 max-w-5xl mx-auto px-6 w-full text-center">
        <div className="flex flex-col items-center text-center gap-8">
          {/* Separator */}
          <div className="w-12 h-px bg-[#a79c68]/80 mx-auto mb-2" />
          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            <span className="text-white">Our </span>
            <span className="text-[#a79c68]">Products</span>
          </h2>

          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 w-full">
            {combos.map((combo, index) => (
              <div key={index} className="flex flex-col gap-4">
                {/* Combo Image with Title Badge */}
                <div
                  className="relative aspect-[4/3] rounded-xl overflow-hidden bg-black cursor-pointer transition-transform hover:scale-105 border-2 border-[#f5f0e8]"
                  onClick={() => handleImageClick(combo)}
                >
                  <img
                    src={combo.image}
                    alt={combo.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                  />
                  {/* Title badge overlapping the image */}
                  <div className="absolute top-0 left-0 right-0 flex justify-center">
                    <h3 className="bg-[#395839] text-white text-lg sm:text-xl md:text-2xl font-bold px-6 py-2 rounded-b-lg">
                      {combo.title}
                    </h3>
                  </div>
                </div>

                {/* Price */}
                <p className="text-xl font-semibold text-gray-300 text-center">
                  {combo.price}
                </p>
              </div>
            ))}
          </div>

          {/* Single CTA Button Below Both Combos */}
          <div className="flex justify-center mt-4">
            <Button
              variant="primary"
              size="lg"
              onClick={() => onCtaClick("Combo Deals")}
            >
              {ctaText}
            </Button>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        imageUrl={lightboxImage?.url || ""}
        imageAlt={lightboxImage?.alt || ""}
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
        ctaText={ctaText}
        onCtaClick={() => {
          if (lightboxImage) {
            onCtaClick(lightboxImage.alt);
          }
        }}
      />
    </section>
  );
}
